/* eslint-disable brace-style */
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, Validators, NgForm, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';

import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';

import {
  takeUntil,
  startWith,
  filter,
  map,
  take,
  first,
  withLatestFrom,
  debounceTime,
  catchError,
} from 'rxjs/operators';

import { Admission, CropAid, ProductTypeEnum } from 'src/app/crop-aid/crop-aid.model';

import {
  SalesOrderPeriod,
  TreatmentType,
} from 'src/app/sales-order-period/models/sales-order-period.models';

import { ApiService } from 'src/app/shared/services/api.service';
import { PhosphateLegalWorkingCoefficientService } from 'src/app/shared/services/phosphate-legal-working-coefficient.service';

import { getCropYear, selectCompanySettings } from 'src/app/store/selectors';

import {
  CropAidAdvice,
  AidAdvice,
  AidAdviceWithNitrogenEffectivity,
  TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
  Objective,
  SiteState,
  ExecutedTreatmentWithNitrogenEffectivity,
  TreatmentWithNitrogenEffectivity,
} from '../../../shared/models';

import {
  EditingTreatmentApi,
  FertilizerReportItemApi,
  FertilizerTable,
} from '../../models/fertilization-result.model';
import { selectNitrogenEffectivity } from '../../store/nitrogen-effectivity.selectors';

import {
  selectExecutedTreatments,
  selectRecommendedNutrientQuantityWithIds,
} from '../../store/selectors';

import { selectEditingTreatments } from '../../store/treatment.selectors';

export interface TreatmentDialogModel extends AbstractFormDialogData {
  treatment: TreatmentOrExecutedTreatmentWithNitrogenEffectivity;
  cropAids: CropAid[];
  areaInHectares: number;
  salesOrderPeriods: SalesOrderPeriod[];
  pastSalesOrderPeriods: SalesOrderPeriod[];
  treatmentType: TreatmentType;
  objectives: Objective[];
  isAnExecutedTreatment: boolean;
  cropId: number;
  adviceId: number;
  cropfieldId: number;
  isPlanetProof: boolean;
  province?: string;
}

const DEFAULT_DRIFT_REDUCTION = 75;

@Component({
  selector: 'app-treatment-dialog',
  templateUrl: './treatment-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./treatment-dialog.component.scss'],
  standalone: false,
})
export class TreatmentDialogComponent
  extends AbstractFormDialog<TreatmentOrExecutedTreatmentWithNitrogenEffectivity>
  implements OnInit, OnDestroy
{
  data = inject<TreatmentDialogModel>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly phosphateWCService = inject(PhosphateLegalWorkingCoefficientService);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
  inject<MatDialogRef<TreatmentDialogComponent, AbstractDialogResultData<TreatmentOrExecutedTreatmentWithNitrogenEffectivity>>>(MatDialogRef);
  private readonly locale = inject(LocaleService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly api = inject(ApiService);


  public TreatmentType = TreatmentType;

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  companySettingsDriftReduction: number;
  selectedProduct: CropAid;
  hasRequested = false;
  showPhosphateWorkingCoefficientWarning = false;
  cropYear$ = this.store.pipe(select(getCropYear));
  executedTreatments$ = this.store.pipe(select(selectExecutedTreatments));
  editingTreatments$ = this.store.pipe(select(selectEditingTreatments));
  recommendedNutrientQuantityWithIds$ = this.store.pipe(
    select(selectRecommendedNutrientQuantityWithIds),
  );
  activeProducts: CropAid[];
  filteredProducts$: Observable<CropAid[]>;
  ngForm: NgForm;
  driftReductions = [75, 90, 95, 97.5, 99];
  aidAdvicesFormArray = this.formBuilder.array(
    this.data.treatment.aidAdvices.map((aidAdvice) => this.getCropAidAdviceFormGroup(aidAdvice)),
    [Validators.required],
  );
  showFertilizationDataDetails$ = new BehaviorSubject(false);
  editedProduct: number;
  addingProduct = false;
  formGroup = this.formBuilder.group({
    id: [this.data.treatment.id],
    shouldUseCompanySettingsDriftReduction: [
      this.data.treatment.shouldUseCompanySettingsDriftReduction ?? true,
    ],
    moment: [this.data.treatment.moment, [Validators.maxLength(4000)]],
    pointOfAttention: [this.data.treatment.pointOfAttention, [Validators.maxLength(4000)]],
    frequency: [
      this.data.treatment.frequency,
      [Validators.required, Validators.min(1), Validators.max(20)],
    ],
    name: [
      this.data.treatment.name,
      [Validators.required, Validators.minLength(1), Validators.maxLength(100)],
    ],
    isAnExecutedTreatment: [this.data.isAnExecutedTreatment],
    periodId: [this.data.treatment.periodId, [Validators.required]],
    objective: [
      this.data.treatment.objective || this.data.objectives[0].value,
      [Validators.required, Validators.minLength(1), Validators.maxLength(4000)],
    ],
    areaInHectaresManual: [
      this.data.treatment.areaInHectaresManual || this.data.treatment.areaInHectaresManual === 0
        ? this.data.treatment.areaInHectaresManual
        : this.data.areaInHectares,
      [Validators.required, Validators.min(0)],
    ],
    shouldUseAreaInHectaresManual: [this.data.treatment.shouldUseAreaInHectaresManual],
    aidAdvices: this.aidAdvicesFormArray,
    treatmentType: this.data.treatmentType,
    driftReduction: [],
  });
  aidAdviceFormGroup: FormGroup = this.newAidAdviceFormGroup();
  treatmentValue$: Observable<TreatmentOrExecutedTreatmentWithNitrogenEffectivity> =
    this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      map((treatment: TreatmentOrExecutedTreatmentWithNitrogenEffectivity) => ({
        ...treatment,
        isAnExecutedTreatment: this.data.treatment.id
          ? this.data.isAnExecutedTreatment
          : treatment.isAnExecutedTreatment,
      })),
    );
  isFertilizationTreatment = this.data.treatmentType === TreatmentType.Fertilization;
  salesOrderPeriods = this.data.salesOrderPeriods;
  cropId = this.data.cropId;
  province = this.data.province;
  tableReportData$: BehaviorSubject<FertilizerTable[]> = new BehaviorSubject<FertilizerTable[]>([]);
  cropAidAdvices: CropAidAdvice[];

  async ngOnInit() {
    this.store
      .select(selectCompanySettings)
      .pipe(first())
      .subscribe((cs) => {
        if (!cs) {
          return;
        }
        this.companySettingsDriftReduction = this.getDefaultDriftReduction(cs.driftReduction);
        const driftReductionFormControl = this.formGroup.get('driftReduction');
        const newDriftReduction =  this.data.treatment.driftReduction || this.companySettingsDriftReduction;
        driftReductionFormControl.setValue(
          newDriftReduction,
        );
        driftReductionFormControl.addValidators(this.isGbmTreatment() ? [Validators.required] : []);
      });

    combineLatest([
      this.treatmentValue$,
      this.executedTreatments$,
      this.recommendedNutrientQuantityWithIds$,
      this.editingTreatments$,
    ])
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(1000),
        withLatestFrom(this.cropYear$),
        map(
          ([
            [treatmentValue, executedTreatments, recommendedNutrientQuantity, editingTreatments],
            cropYear,
          ]) => {
            this.formGroup.updateValueAndValidity({ emitEvent: false });
            if (!this.everyAidAdviceValid() || !this.formGroup.valid) {
              return EMPTY;
            }

            const adviceId = this.data.adviceId;
            if (!adviceId || adviceId <= 0 || !cropYear) {
              return EMPTY;
            }

            return this.api
              .getFertilizationData(
                cropYear,
                recommendedNutrientQuantity?.find(
                  (rn) => rn?.cropFieldId === this.data.cropfieldId,
                ),
                adviceId,
                this.combineTreatments(executedTreatments, editingTreatments, treatmentValue),
              )
              .pipe(
                map((result) => this.tableReportData$.next([this.mapApiResultToTableData(result)])),
                catchError((_) => {
                  this.store.dispatch(
                    NotificationActions.errorSnackbar({
                      message: this.locale.translate('errorGettingFertilizationData'),
                    }),
                  );
                  return of({});
                }),
              )
              .subscribe();
          },
        ),
      )
      .subscribe();

    this.cropAidAdvices = this.data.treatment.aidAdvices.map((a) => ({
      ...this.findProduct(a.cropAidId),
      ...a,
    }));

    this.activeProducts = this.data.cropAids.filter((cropAid) => cropAid.isActive);
    this.formGroup
      .get('periodId')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((_) => {
        this.aidAdvicesFormArray.controls.forEach((control) => {
          const nitrogenEffectivityControl = control.get('nitrogenEffectivity');
          const shouldUseNitrogenEffectivityManualControl = control.get(
            'shouldUseNitrogenEffectivityManual',
          );
          const cropAidIdControl = control.get('cropAidId');
          if (!shouldUseNitrogenEffectivityManualControl.value) {
            const cropAid = this.data.cropAids.find(({ id }) => id === cropAidIdControl.value);
            const nitrogenEffectivity = this.getNitrogenEffectivity(cropAid);
            nitrogenEffectivityControl.setValue(nitrogenEffectivity, {
              emitEvent: false,
            });
          }
        });
      });
    this.formGroup
      .get('areaInHectaresManual')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((_) => {
        this.formGroup.get('shouldUseAreaInHectaresManual').setValue(true);
      });
    this.formGroup
      .get('shouldUseAreaInHectaresManual')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((shouldUseAreaInHectaresManual) => {
        if (!shouldUseAreaInHectaresManual) {
          this.formGroup
            .get('areaInHectaresManual')
            .setValue(this.data.areaInHectares, { emitEvent: false });
        }
      });
    this.updateSalesOrderPeriod();
    this.formGroup
      .get('isAnExecutedTreatment')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.formGroup.get('periodId').setValue(null);
        this.updateSalesOrderPeriod();

        const id = this.formGroup.get('id').value;
        if (!id) {
          if (this.formGroup.get('isAnExecutedTreatment').value) {
            this.formGroup.get('id').setValue(0);
          } else {
            this.formGroup.get('id').setValue('');
          }
        }
      });
    if (this.data.treatment.aidAdvices.length === 0) {
      this.addProduct();
    }
  }

  shouldUseDriftReductionToggle(toggleEvent: MatSlideToggleChange) {
    if (toggleEvent.checked) {
      this.formGroup.get('driftReduction').setValue(this.companySettingsDriftReduction);
    }
  }

  updateSalesOrderPeriod() {
    this.salesOrderPeriods = this.formGroup.get('isAnExecutedTreatment').value
      ? this.data.pastSalesOrderPeriods
      : this.data.salesOrderPeriods;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  filterProducts(value: string): CropAid[] {
    const filterValue = value.toLowerCase();
    return this.data.cropAids
      .filter((cropAid) => cropAid.isActive)
      .filter((cropAid) => !cropAid.isHidden)
      .filter((cropAid) => this.isCropAidOfSameTypeAsTreatment(cropAid))
      .filter((cropAid) => cropAid.name.toLowerCase().includes(filterValue));
  }

  isCropAidOfSameTypeAsTreatment(cropAid: CropAid) {
    return this.isGbmTreatment() ? cropAid.isCropProtection : cropAid.isFertilizer;
  }

  isGbmTreatment() {
    return String(ProductTypeEnum.GBM) === String(this.data.treatment.treatmentType);
  }

  toggleFertilizationDetails() {
    this.showFertilizationDataDetails$.pipe(take(1)).subscribe((showFertilizationDetails) => {
      this.showFertilizationDataDetails$.next(!showFertilizationDetails);
    });
  }

  getCropAidAdviceFormGroup(aidAdvice: AidAdviceWithNitrogenEffectivity) {
    const cropAid = this.data.cropAids.find(({ id }) => id === aidAdvice.cropAidId);
    const nitrogenEffectivity = this.getNitrogenEffectivity(cropAid);
    const formGroup = this.formBuilder.group({
      cropAidId: [aidAdvice.cropAidId, [Validators.pattern(/^\d+$/), Validators.required]],
      dosage: [
        aidAdvice.dosage,
        [
          Validators.required,
          Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/),
          Validators.min(0.001),
          Validators.max(10000000),
        ],
      ],
      nitrogenEffectivity: aidAdvice.shouldUseNitrogenEffectivityManual
        ? aidAdvice.nitrogenEffectivity
        : nitrogenEffectivity,
      shouldUseNitrogenEffectivityManual: aidAdvice.shouldUseNitrogenEffectivityManual,
    });

    formGroup
      .get('nitrogenEffectivity')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((_) => {
        formGroup.get('shouldUseNitrogenEffectivityManual').setValue(true);
      });

    formGroup
      .get('shouldUseNitrogenEffectivityManual')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((shouldUseNitrogenEffectivityManual) => {
        if (!shouldUseNitrogenEffectivityManual) {
          const foundCropAid = this.data.cropAids.find(
            ({ id }) => id === formGroup.controls.cropAidId.value,
          );
          const foundNitrogenEffectivity = this.getNitrogenEffectivity(foundCropAid);
          formGroup.get('nitrogenEffectivity').setValue(foundNitrogenEffectivity, {
            emitEvent: false,
          });
          formGroup.get('nitrogenEffectivity').setValidators([]);
        } else {
          formGroup
            .get('nitrogenEffectivity')
            .setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        }
        formGroup.get('nitrogenEffectivity').updateValueAndValidity({ emitEvent: false });
      });

    formGroup.controls.cropAidId.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(''),
        filter((value) => typeof value === 'string'),
      )
      .subscribe((value: string) => {
        this.filteredProducts$ = of(this.filterProducts(value));
      });

    return formGroup;
  }

  getNitrogenEffectivity(cropAid: CropAid): unknown {
    return selectNitrogenEffectivity({
      cropAid,
      periodId: this.formGroup?.controls.periodId.value || this.data.treatment.periodId,
      isAnExecutedTreatment: this.formGroup?.get('isAnExecutedTreatment')
        ? this.formGroup.get('isAnExecutedTreatment').value
        : this.data.treatment.isAnExecutedTreatment,
    }).projector(this.data.salesOrderPeriods, this.data.pastSalesOrderPeriods);
  }

  newAidAdviceFormGroup() {
    const formGroup = this.getCropAidAdviceFormGroup({
      cropAidId: null,
      dosage: null,
      shouldUseNitrogenEffectivityManual: false,
      nitrogenEffectivity: null,
    });
    return formGroup;
  }

  displayProduct() {
    return (cropAidId?: number) => (cropAidId ? this.findProduct(cropAidId)?.name : '');
  }

  setNitrogenEffectivity() {
    const nitrogenEffectivity = this.getNitrogenEffectivity(this.selectedProduct);
    if (nitrogenEffectivity) {
      this.aidAdviceFormGroup.controls.nitrogenEffectivity.setValue(nitrogenEffectivity, {
        emitEvent: false,
      });
    }
  }

  selectProduct(cropAidId: number, updateSelectedProduct = false) {
    this.setPhosphateWorkingCoefficientWarning(cropAidId);
    if (updateSelectedProduct) {
      this.selectedProduct = this.findProduct(cropAidId);
    }

    this.setNitrogenEffectivity();
    if (!this.formGroup.controls.name.value) {
      this.formGroup.controls.name.setValue(
        this.data.cropAids.find(({ id }) => cropAidId === id)?.name,
      );
    }
  }

  findProduct(cropAidId: number): CropAid {
    return this.data.cropAids.find((cropAid) => cropAid.id === cropAidId);
  }

  addProduct() {
    this.aidAdvicesFormArray.push(this.aidAdviceFormGroup);
    this.addingProduct = true;
  }

  doneAddingProduct() {
    if (this.aidAdviceFormGroup.valid && this.selectedProduct) {
      const result = this.aidAdviceFormGroup.value as AidAdvice;
      const cropAidAdvice = { ...this.findProduct(result.cropAidId), ...result };
      this.cropAidAdvices.push(cropAidAdvice);
      this.addingProduct = false;
      this.selectedProduct = null;
      this.aidAdviceFormGroup = this.newAidAdviceFormGroup();
      this.filteredProducts$ = of(this.data.cropAids);
    }
  }

  cancelAddingProduct() {
    this.addingProduct = false;
    this.aidAdviceFormGroup.reset();
    this.aidAdvicesFormArray.removeAt(this.aidAdvicesFormArray.length - 1);
    this.selectedProduct = null;
    this.filteredProducts$ = of(this.data.cropAids);
  }

  checkProduct() {
    if (!this.selectedProduct) {
      this.aidAdviceFormGroup.get('cropAidId').setValue(null);
    }
  }

  editProduct(index: number) {
    this.editedProduct = index;
  }

  doneEditingProduct() {
    if (this.editedProduct) {
      const aidAdvice = this.aidAdvicesFormArray.controls[this.editedProduct].value as AidAdvice;
      const cropAidAdvice = { ...this.findProduct(aidAdvice.cropAidId), ...aidAdvice };
      this.cropAidAdvices[this.editedProduct] = cropAidAdvice;
    }
    this.editedProduct = null;
    this.filteredProducts$ = of(this.data.cropAids);
  }

  removeProduct(i: number) {
    this.cropAidAdvices.splice(i, 1);
    this.aidAdvicesFormArray.removeAt(i);
    if (this.aidAdvicesFormArray.length === 0) {
      this.addProduct();
    }
  }

  getIndicativePrice(indicativePrice: number) {
    return indicativePrice / 100;
  }

  getAdmissionNumberString(admissions: Admission[]) {
    if (!admissions || admissions.length < 1) {
      return;
    }

    return `(${admissions.map((admission) => admission.number).join(', ')})`;
  }

  everyAidAdviceValid() {
    return (
      this.cropAidAdvices.find((cropAidAdvices) => !this.isCropAidValid(cropAidAdvices)) == null
    );
  }

  setNitrogenEffectivityValue($event: TreatmentOrExecutedTreatmentWithNitrogenEffectivity) {
    $event.aidAdvices.forEach((aidAdvice) => {
      aidAdvice.nitrogenEffectivity = aidAdvice.shouldUseNitrogenEffectivityManual
        ? aidAdvice.nitrogenEffectivity
        : null;
    });
    return $event;
  }

  confirm($event: TreatmentOrExecutedTreatmentWithNitrogenEffectivity) {
    this.doneAddingProduct();
    if (!$event.aidAdvices.length || !this.everyAidAdviceValid()) {
      return;
    }

    $event = this.setNitrogenEffectivityValue($event);

    super.confirm({
      ...$event,
    });
  }

  trackByFn(index: number) {
    return index;
  }

  isCropAidValid = (cropAid: CropAid): boolean => this.getCropAidInvalidMessage(cropAid) === null;

  getCropAidInvalidMessage = (cropAid: CropAid): string => {
    if (!cropAid) {
      return null;
    }
    if (cropAid.isActive === false) {
      return this.locale.translate('cropAidInactive');
    }
    if (this.data.isPlanetProof && cropAid.forbiddenForPlanetProof) {
      return this.locale.translate('cropAidForbiddenInPlanetProof');
    }
    return null;
  };

  setPhosphateWorkingCoefficientWarning(selectedCropAidId: number) {
    this.cropYear$.subscribe((cropYear) =>
      this.phosphateWCService
        .getPhosphateLegalWorkingCoefficientByCropAidIdAndYear(selectedCropAidId, cropYear)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res) => {
            this.showPhosphateWorkingCoefficientWarning = res.phosphateWorkingCoefficient < 100;
            this.cdRef.detectChanges();
          },
          error: (_) => {
            this.showPhosphateWorkingCoefficientWarning = false;
            this.cdRef.detectChanges();
          },
        }),
    );
  }

  private getDefaultDriftReduction(driftReduction?: number) {
    return this.isGbmTreatment() ? driftReduction || DEFAULT_DRIFT_REDUCTION : null;
  }

  private combineTreatments(
    executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[],
    editingTreatments: TreatmentWithNitrogenEffectivity[],
    treatmentValue: TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
  ): EditingTreatmentApi[] {
    const mappedTreatments: EditingTreatmentApi[] = [
      ...executedTreatments.map((t) => ({
        ...t,
        isPrimary: false,
      })),
      ...editingTreatments.map((t) => ({
        ...t,
        isPrimary: false,
      })),
    ].filter((t) => t.id !== treatmentValue.id);

    mappedTreatments.push({ ...treatmentValue, isPrimary: true });

    return mappedTreatments;
  }

  private mapApiResultToTableData(item: FertilizerReportItemApi) {
    return {
      metaData: {
        name: item.name,
        cropName: item.cropName,
        cropVariety: item.cropVariety,
        area: item.area,
      },
      fertilizationItems: item.fertilizationItems
        .filter((fItem) => fItem.primary)
        .map((fItem) => ({
          source: fItem.source,
          data: fItem.fertilizerData,
          primary: fItem.primary,
          isExecuted: fItem.isExecuted,
          note: fItem.note,
          displayDosage: fItem.displayDosage,
        })),
    };
  }
}
